import React, { useMemo } from 'react';
import { Paper, Stack, FormControl, InputLabel, Select, MenuItem, Button, TextField } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faRefresh } from '@fortawesome/free-solid-svg-icons';
import SearchableSelect from '../common/SearchableSelect';
import { useTranslation } from 'react-i18next';

const SupervisorFilter = ({
    supervisors,
    supervisorsLoading,
    selectedSupervisorId,
    onSupervisorChange,
    fromDate,
    toDate,
    onFromDateChange,
    onToDateChange,
    onSubmit,
    loading,
    activeTab,
    statusFilter,
    onStatusFilterChange,
    disableSupervisorSelector = false,
}) => {
    const { t } = useTranslation();
    const supervisorOptions = useMemo(() =>
        (supervisors || []).map((s) => ({
            value: s.id,
            label: `${s.first_name || ''} ${s.last_name || ''}`.trim() || s.username || s.email || String(s.id),
        })),
        [supervisors]
    );

    const statusOptions = [
        { value: '', label: t('supervisorAnalytics.filters.allStatuses') },
        { value: 'loaded', label: t('supervisorAnalytics.filters.loaded') },
        { value: 'in_transit', label: t('supervisorAnalytics.filters.inTransit') },
        { value: 'delivered', label: t('supervisorAnalytics.filters.delivered') },
        { value: 'pending', label: t('supervisorAnalytics.filters.pending') },
    ];

    const unloadingStatusOptions = [
        { value: '', label: t('supervisorAnalytics.filters.allStatuses') },
        { value: 'unloaded', label: t('supervisorAnalytics.filters.unloaded') },
        { value: 'pending', label: t('supervisorAnalytics.filters.pending') },
    ];

    const workloadStatusOptions = [
        { value: '', label: t('supervisorAnalytics.filters.allStatuses') },
        { value: 'PENDING', label: t('supervisorAnalytics.filters.pending') },
        { value: 'SUPERVISOR_ASSIGNED', label: t('supervisorAnalytics.filters.assigned') },
        { value: 'LOADING', label: t('supervisorAnalytics.filters.loading') },
        { value: 'LOADED', label: t('supervisorAnalytics.filters.loaded') },
    ];

    const currentStatusOptions = activeTab === 'unloading' ? unloadingStatusOptions
        : activeTab === 'workload' ? workloadStatusOptions
        : statusOptions;

    return (
        <Paper
            elevation={0}
            sx={{
                p: 3,
                mb: 3,
                borderRadius: 4,
                boxShadow: '0 4px 20px rgba(0,0,0,0.05)',
                border: '1px solid rgba(0,0,0,0.08)',
                bgcolor: '#FFFFFF',
            }}
        >
            <Stack
                direction={{ xs: 'column', sm: 'row' }}
                spacing={2}
                alignItems={{ xs: 'stretch', sm: 'center' }}
                flexWrap="wrap"
                useFlexGap
            >
                {/* Supervisor selector — hidden on leaderboard tab */}
                {activeTab !== 'leaderboard' && (
                    <div style={{ minWidth: 220 }}>
                        <SearchableSelect
                            options={supervisorOptions}
                            value={selectedSupervisorId}
                            onChange={(e) => onSupervisorChange(e.target.value)}
                            placeholder={t('supervisorAnalytics.filters.selectSupervisor')}
                            disabled={supervisorsLoading || disableSupervisorSelector}
                            loading={supervisorsLoading}
                        />
                    </div>
                )}

                {/* Date range */}
                <TextField
                    label={t('supervisorAnalytics.filters.fromDate')}
                    type="date"
                    value={fromDate}
                    onChange={(e) => onFromDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />
                <TextField
                    label={t('supervisorAnalytics.filters.toDate')}
                    type="date"
                    value={toDate}
                    onChange={(e) => onToDateChange(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    sx={{ minWidth: 160, ...textFieldSx }}
                />

                {/* Status filter — only for loading/unloading tabs */}
                {(activeTab === 'loading' || activeTab === 'unloading' || activeTab === 'workload') && (
                    <FormControl size="small" sx={{ minWidth: 160 }}>
                        <InputLabel>{t('supervisorAnalytics.filters.status')}</InputLabel>
                        <Select
                            value={statusFilter}
                            label={t('supervisorAnalytics.filters.status')}
                            onChange={(e) => onStatusFilterChange(e.target.value)}
                            sx={selectSx}
                        >
                            {currentStatusOptions.map((o) => (
                                <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                )}

                {/* Apply button */}
                <Button
                    variant="contained"
                    onClick={onSubmit}
                    disabled={loading || !fromDate || !toDate}
                    startIcon={<FontAwesomeIcon icon={loading ? faRefresh : faSearch} className={loading ? 'animate-spin' : ''} />}
                    sx={{
                        minWidth: 120,
                        height: 40,
                        borderRadius: 2,
                        textTransform: 'none',
                        fontWeight: 600,
                        bgcolor: '#189AB4',
                        boxShadow: 'none',
                        '&:hover': { bgcolor: '#05445E', boxShadow: '0 4px 12px rgba(5,68,94,0.3)' },
                    }}
                >
                    {loading ? t('supervisorAnalytics.filters.loadingText') : t('supervisorAnalytics.filters.apply')}
                </Button>
            </Stack>
        </Paper>
    );
};

const selectSx = {
    borderRadius: 2,
    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#75E6DA' },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#189AB4' },
};

const textFieldSx = {
    '& .MuiOutlinedInput-root': {
        borderRadius: 2,
        '&:hover fieldset': { borderColor: '#75E6DA' },
        '&.Mui-focused fieldset': { borderColor: '#189AB4' },
    },
};

export default SupervisorFilter;
